import { Component } from 'opus-angular-seed-core';
import template from './template.html';
import { loadFiltersOptions } from './helpers';

const defaultMultiselectSettings = {
    checkBoxes: true,
    showCheckAll: true,
    showUncheckAll: true,
    smartButtonMaxItems: 3,
    smartButtonTextConverter: (itemText) => itemText
};

@Component({
    bindings: {
        defaultCourse: '<',
        classifiers: '=',
        sites: '=',
        trainingFields: '=',
        list: '=',
        course: '=',
        filterParams: '=',
        onFilterApplied: '&'
    },
    template
})
export default class {
    constructor (
        $translate,
        CourseResource,
        COURSE_STATUS_TYPE_ENUM,
        USER_STATUS_ENUM,
        COURSE_TARGET_NEED_TYPE_ENUM,
        ListFactory,
        ClassifierResource,
        FilterService,
        UserResource,
        ROLE_CODE_ENUM,
        DebounceService,
        $window,
        SearchResource,
        MomentService,
        moment
    ) {
        this.$translate = $translate;
        this.courseResource = CourseResource;
        this.MomentService = MomentService;
        this.moment = moment;
        this.ListFactory = ListFactory;
        this.ClassifierResource = ClassifierResource;
        this.FilterService = FilterService;
        this.COURSE_STATUS_TYPE_ENUM = COURSE_STATUS_TYPE_ENUM;
        this.USER_STATUS_ENUM = USER_STATUS_ENUM;
        this.COURSE_TARGET_NEED_TYPE_ENUM = COURSE_TARGET_NEED_TYPE_ENUM;
        this.UserResource = UserResource;
        this.ROLE_CODE_ENUM = ROLE_CODE_ENUM;
        this.DebounceService = DebounceService;
        this.$window = $window;
        this.SearchResource = SearchResource;
    }

    $onInit () {
        this.initFiltersOptions();
        this.buildDefaultFilters();
        this.applyFilter();

        this.courseSearchResults = [];
        this.courseSearchCompleted = false;
        this.course = this.defaultCourse;

        this.debouncedQueryUsers = this.DebounceService.create(
            (query) => this.queryUsers(query),
            { minLength: 3, delay: 500 }
        );
        this.debouncedFilterManagers = this.DebounceService.create(
            (query) => this.filterManagers(query),
            { minLength: 3, delay: 500 }
        );
        this.debouncedSearchCourses = this.DebounceService.create(
            (query) => this.searchCourses(query),
            { minLength: 3, delay: 300 }
        );
        this.filterTranslations = this.FilterService.getTranslations();
        this.maximumTagsInInput = 3;
    }

    initFiltersOptions () {
        this.FilterService.init(this.classifiers);

        const {
            courseStatus,
            userStatusOptions,
            siteOptions,
            departmentOptions,
            roleOptions,
            levelOptions,
            participationOptions,
            trainingFieldOptions
        } = loadFiltersOptions(
            {
                courseStatusEnum: this.COURSE_STATUS_TYPE_ENUM,
                userStatusEnum: this.USER_STATUS_ENUM,
                sites: this.sites,
                participationEnum: this.COURSE_TARGET_NEED_TYPE_ENUM,
                trainingFields: this.trainingFields,
                departments: this.FilterService.departmentSelect.options,
                roles: this.FilterService.roleSelect.options,
                levels: this.FilterService.levelSelect.options
            }
        );

        this.courseStatusOptions = courseStatus;
        this.userStatusOptionsMap = userStatusOptions;
        this.siteOptionsMap = siteOptions;
        this.departmentOptionsMap = departmentOptions;
        this.roleOptionsMap = roleOptions;
        this.levelOptionsMap = levelOptions;
        this.participationOptionsMap = participationOptions;
        this.trainingFieldOptionsMap = trainingFieldOptions;
    }

    buildDefaultFilters () {
        this.course = this.defaultCourse;
        const { activeI18n } = this.course;
        const { name: courseName } = activeI18n;
        this.courseInputText = courseName;

        const savedFilterRaw = this.$window.localStorage.getItem('courseresults:filter');
        this.filterParams = savedFilterRaw ? JSON.parse(savedFilterRaw) : {};

        const {
            from,
            to,
            employeeStatuses: employeeStatusIds,
            siteIds,
            uDepIds: departmentIds,
            uRoleIds: roleIds,
            uLevelIds: levelIds,
            participationTypes: participation,
            trainingFieldIds
        } = this.filterParams;

        const savedChosenEmployeesRaw = this.$window.localStorage.getItem('courseresults:chosenEmployees');
        const savedChosenManagersRaw = this.$window.localStorage.getItem('courseresults:chosenManagers');
        const employees = savedChosenEmployeesRaw ? JSON.parse(savedChosenEmployeesRaw) : [];
        const directManagers = savedChosenManagersRaw ? JSON.parse(savedChosenManagersRaw) : [];

        const defaultFrom = from || this.MomentService.format(this.moment().subtract(60, 'days'), 'YYYY-MM-DD');
        const defaultTo = to || this.MomentService.format(this.moment(), 'YYYY-MM-DD');
        this.defaultFilter = Object.assign({}, { courseName, from: defaultFrom, to: defaultTo });
        this.chosenEmployees = [...employees];
        this.chosenManagers = [...directManagers];

        this.buildMultiSelectFilters({
            employeeStatusIds,
            siteIds,
            departmentIds,
            roleIds,
            levelIds,
            participation,
            trainingFieldIds
        });

        this.filter = this.defaultFilter;
    }

    buildMultiselectFilter ({
        options,
        selectedOptions = [],
        settings = {},
        defaultSettings = defaultMultiselectSettings
    }) {
        return {
            options,
            model: selectedOptions,
            settings: Object.assign({}, defaultSettings, settings)
        };
    }

    buildMultiSelectFilters ({
        employeeStatusIds = [],
        siteIds = [],
        departmentIds = [],
        roleIds = [],
        levelIds = [],
        participation = [],
        trainingFieldIds = []
    } = {}) {
        const courseStatusSelectedOptions = [...this.courseStatusOptions];
        this.courseStatusFilter = this.buildMultiselectFilter({
            options: this.courseStatusOptions,
            selectedOptions: courseStatusSelectedOptions
        });
        this.employeeStatusFilter = this.buildMultiselectFilter({
            options: Object.values(this.userStatusOptionsMap),
            selectedOptions: employeeStatusIds.map((statusId) => this.userStatusOptionsMap[statusId])
        });
        this.employeeSiteFilter = this.buildMultiselectFilter({
            options: Object.values(this.siteOptionsMap).sort((a, b) => a.label.localeCompare(b.label)),
            selectedOptions: siteIds.map((siteId) => this.siteOptionsMap[siteId])
        });

        this.FilterService.departmentSelect.models = departmentIds.length
            ? departmentIds.map((deptId) => this.departmentOptionsMap[deptId])
            : [this.FilterService.departmentSelect.options[0]];
        this.FilterService.roleSelect.models = roleIds.length
            ? roleIds.map((roleId) => this.roleOptionsMap[roleId])
            : [this.FilterService.roleSelect.options[0]];
        this.FilterService.levelSelect.models = levelIds.length
            ? levelIds.map((levelId) => this.levelOptionsMap[levelId])
            : [this.FilterService.levelSelect.options[0]];

        this.employeeParticipationFilter = this.buildMultiselectFilter({
            options: Object.values(this.participationOptionsMap),
            selectedOptions: participation.map((prtpn) => this.participationOptionsMap[prtpn])
        });
        const trainingFieldOptionsSorted = Object.values(this.trainingFieldOptionsMap)
            .sort((a, b) => a.label.localeCompare(b.label));
        this.trainingFieldFilter = this.buildMultiselectFilter({
            options: trainingFieldOptionsSorted,
            selectedOptions: trainingFieldIds.map((trainingFieldId) => this.trainingFieldOptionsMap[trainingFieldId]),
            settings: {
                enableSearch: true
            }
        });
    }

    resetFilter () {
        this.employeeLimitError = null;
        this.managerLimitError = null;
        this.buildDefaultFilters();
        this.applyFilter();
    }

    prepareFilterPayload () {
        const payload = Object.assign({}, this.filter);
        const { id } = this.course;
        const { from, to } = payload;
        const statuses = this.courseStatusFilter.model.map((status) => status.value);
        const sites = this.employeeSiteFilter.model.map((site) => site.value);
        const employeeStatuses = this.employeeStatusFilter.model.map((status) => status.value);
        const departments = this.FilterService.departmentSelect.models.map((dept) => dept.id).filter(Boolean);
        const roles = this.FilterService.roleSelect.models.map((role) => role.id).filter(Boolean);
        const levels = this.FilterService.levelSelect.models.map((level) => level.id).filter(Boolean);
        const participations = this.employeeParticipationFilter.model.map((participation) => participation.value);
        const trainingFields = this.trainingFieldFilter.model.map((trainingField) => trainingField.value);

        const departmentIds = departments.length ? { departmentIds: departments } : {};
        const roleIds = roles.length ? { roleIds: roles } : {};
        const levelIds = levels.length ? { levelIds: levels } : {};

        return Object.assign({},
            {
                employeeIds: this.chosenEmployees.map((employee) => employee.id),
                courseId: id,
                status: statuses,
                from,
                to,
                directManagerIds: this.chosenManagers.map((manager) => manager.id),
                employeeStatus: employeeStatuses,
                siteIds: sites,
                participationTypes: participations,
                trainingField: trainingFields
            },
            departmentIds,
            roleIds,
            levelIds
        );
    }

    applyFilter () {
        const payload = this.prepareFilterPayload();
        this.filterParams = payload;
        return this.list.fetch({ expand: 'drlsWithValue' }, payload).then(() => {
            if (this.onFilterApplied) {
                this.onFilterApplied();
            }
        });
    }

    queryUsers (query) {
        return this.UserResource.query({
            name: query,
            pageSize: 10
        }).$promise.then(data => {
            const items = data.items || data;
            return items.map(user => (Object.assign({}, user, { customName: user.displayName })));
        });
    }

    filterManagers (query) {
        return this.UserResource.query({
            name: query,
            roleCode: this.ROLE_CODE_ENUM.KEYS.DIRECT_MANAGER,
            pageSize: 10
        }).$promise.then(data => {
            const items = data.items || data;
            return items.map(user => (Object.assign({}, user, { customName: user.displayName })));
        });
    }

    searchCourses (keyword) {
        if (!keyword || !keyword.length) {
            this.courseSearchResults = [];
            this.courseSearchCompleted = false;
            return Promise.resolve([]);
        }

        if (keyword.length < 3) {
            return Promise.resolve([]);
        }

        return this.SearchResource.getAutocompleteResults({ keyword }).$promise.then((results) => {
            this.courseSearchResults = results.courses || [];
            this.courseSearchCompleted = true;
            return this.courseSearchResults;
        });
    }

    onCourseSelected (item) {
        this.course = item;
        this.courseInputText = item.name;
        this.filter.courseName = item.name;
    }

    onCourseInputChange () {
        if (!this.courseInputText) {
            this.course = null;
            this.filter.courseName = null;
        }
    }

    selectCourse (course) {
        this.course = course;
        this.filter.courseName = course.name;
        this.courseSearchResults = [];
        this.courseSearchCompleted = false;
    }

    onEmployeeTagAdding () {
        if (this.chosenEmployees && this.chosenEmployees.length >= 3) {
            this.employeeLimitError = this.$translate.instant('filter.Maximum X selections allowed',
                { maximum: this.maximumTagsInInput }
            );
            return false;
        }
        this.employeeLimitError = null;
        return true;
    }

    onManagerTagAdding () {
        if (this.chosenManagers && this.chosenManagers.length >= 3) {
            this.managerLimitError = this.$translate.instant('filter.Maximum X selections allowed',
                { maximum: this.maximumTagsInInput }
            );
            return false;
        }
        this.managerLimitError = null;
        return true;
    }

}



// WEBPACK FOOTER //
// ./src/app/components/reports/courseresults/learnerProgress/filter/index.js